import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import withModal from '../../../modal/withModal';
import CustomPrintPhoto from '../CustomPrintPhoto';
import Button from '../../../Button';
import Alert from '../../../Alert';
import CustomPhotoUploadTab from './CustomPhotoUploadTab';

const propTypes = {
  defaultPhotoGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      total: PropTypes.number,
      photos: PropTypes.array,
      loadMore: PropTypes.func,
    }),
  ),
  selectedPhoto: PropTypes.shape({
    photo: PropTypes.shape({
      id: PropTypes.string,
    }),
    position: PropTypes.number.isRequired,
  }),
  onPhotoConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  customUploadEnabled: PropTypes.bool,
  purchasedCustomPrintPhotos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
};

const CustomPrintConfiguratorPhotoSelector = ({
  selectedPhoto,
  defaultPhotoGroups = [],
  onPhotoConfirm,
  onClose,
  customUploadEnabled,
  purchasedCustomPrintPhotos = [],
}) => {
  // If the configurator allows for custom uploads add it
  // as a "photoGroup" on top of the default ones.
  const photoGroups = [
    ...defaultPhotoGroups,
    ...(customUploadEnabled
      ? [
          {
            id: 'custom-upload',
            label: 'Upload',
          },
        ]
      : []),
  ];

  const [currentSelectedPhoto, setCurrentSelectedPhoto] = useState(
    selectedPhoto?.photo ?? null,
  );
  const [activePhotoGroupIndex, setActivePhotoGroupIndex] = useState(0);
  const activePhotoGroup = photoGroups[activePhotoGroupIndex] || {};
  const { photos = [], loadMore, total = 0 } = activePhotoGroup;

  const onPhotoSelect = photo => {
    setCurrentSelectedPhoto(photo);
  };

  const onPhotoGroupIndexSwitch = index => {
    setActivePhotoGroupIndex(index);
  };

  const onConfirmSelection = () => {
    onPhotoConfirm(currentSelectedPhoto, selectedPhoto.position);
  };

  return (
    <div className="custom-print-config-photos-select">
      <div className="custom-print-config-select-header">
        <p className="mb-0">
          <strong className="d-block text-dark">
            Select a photo for position {selectedPhoto.position + 1}
          </strong>
          <span className="text-sm text-italic">
            Photos will be printed in high resolution
          </span>
        </p>
      </div>

      {photoGroups.length > 1 && (
        <nav className="custom-print-config-select-nav">
          {photoGroups.map((photoGroup, index) => (
            <button
              key={photoGroup.id}
              className={classNames({
                'custom-print-config-select-nav-item': true,
                'text-primary': index === activePhotoGroupIndex,
              })}
              onClick={() => onPhotoGroupIndexSwitch(index)}
            >
              {photoGroup.label}
            </button>
          ))}
        </nav>
      )}

      {(!photos.length && activePhotoGroup?.id !== 'custom-upload') ||
      !photoGroups.length ? (
        <Alert type="info">
          Whoops! We found no photos for selection. Please try again in a while
          as Waldo is looking for them!
        </Alert>
      ) : (
        <div className="custom-print-config-photo-list-scroll">
          {activePhotoGroup?.id === 'custom-upload' ? (
            <CustomPhotoUploadTab
              onSelect={onPhotoSelect}
              selectedPhoto={
                // Only show if the photo is indeed a custom upload
                currentSelectedPhoto?.custom && currentSelectedPhoto
              }
            />
          ) : (
            <div className="custom-print-config-photo-list">
              {photos.map(record => {
                const photo = record.photo || record;

                return (
                  <CustomPrintPhoto
                    key={record.id}
                    photo={photo}
                    isSelected={photo.id === currentSelectedPhoto?.id}
                    onSelect={onPhotoSelect}
                    isPurchased={
                      !!purchasedCustomPrintPhotos.find(
                        purchasedCustomPrintPhoto =>
                          purchasedCustomPrintPhoto.id === photo.id,
                      )
                    }
                  />
                );
              })}
            </div>
          )}

          {loadMore && photos.length < total && (
            <Button
              btnStyle="link"
              onClick={loadMore}
              btnBlock
              className="mt-3 text-center"
            >
              Load more
            </Button>
          )}
        </div>
      )}

      <div className="mt-auto">
        <Button
          className="text-color-base text-center mt-3"
          btnBlock
          btnStyle="link"
          onClick={onClose}
        >
          Cancel
        </Button>

        <Button
          btnBlock
          onClick={onConfirmSelection}
          className="flex-none mt-2"
        >
          Done
        </Button>
      </div>
    </div>
  );
};

CustomPrintConfiguratorPhotoSelector.propTypes = propTypes;

export default withModal(CustomPrintConfiguratorPhotoSelector, {
  dismissible: false,
  hideHeader: true,
  size: 'sm',
  className: 'custom-print-photo-select-modal',
});
